import { forwardRef, useState, useImperativeHandle, useEffect } from 'react';
import MDCTextField, { HelperText } from '@material/react-text-field';
import useFormInput from '../../../../utils/hooks/useFormInput';
import Icon from '../../MDC/Icon/Icon';
import { Input } from '@material/react-text-field';
import StyleAuxiliary from '../../../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import classnames from 'classnames';
import { CSS_CLASSES } from './_constants';
import PropTypes from 'prop-types';

import './TextField.scss';

export const TextField = forwardRef(function TextField(
	{ withoutFormik, ...props },
	ref
) {
	const { field, meta } = withoutFormik
		? { field: {}, meta: {} } // eslint-disable-next-line react-hooks/rules-of-hooks
		: // eslint-disable-next-line react-hooks/rules-of-hooks
		  useFormInput(props);
	const [sValue, setValue] = useState('');

	let refInput = ref;

	/**
	 * Exposes getValue to parents that are using prop 'ref'. As a function component, this handle is needed to expose any parts of itself as a ref.
	 */
	useImperativeHandle(ref, () => ({
		getValue: () => sValue,
		setValue: value => setValue(value),
	}));

	const onChangeHandler = e => {
		if (props.onChange) {
			props.onChange(e);
		}
		setValue(e.target.value);
	};

	const onClear = () => {
		const e = {
			target: {
				value: '',
			},
		};
		onChangeHandler(e);
		if (refInput) {
			// restore focus to <Input /> after trailing <Icon /> click
			const inputElement = refInput.inputElement;
			if (inputElement) {
				inputElement.focus();
			}
		}
	};

	let {
		canClear,
		floatingLabelClassName,
		help,
		helperText = null,
		inputProps: { customError = null, id = null, ...inputProps } = {},
		onChange,
		trailingIcon,
		valid = 'true',
		...rest
	} = props;

	/**
	 * @summary Lifecycle hook on JSX attribute change. Supports `props`-driven complex constraint validation.
	 * @description Offers custom programmatic control over element validation message. Reconciles scenarios where
	 * validation message changed but focus withdrawn before element can consume change by temporarily giving focus to element then
	 * returning to originally focused element.
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/HTMLObjectElement/setCustomValidity|setCustomValidity()} for more information.
	 *
	 * @function
	 * @return void. No clean-up performed.
	 */
	useEffect(() => {
		if (
			customError !== null &&
			refInput?.inputElement &&
			!customError !== !refInput.inputElement.validity?.customError
		) {
			const activeElement = document.activeElement;
			refInput.inputElement.setCustomValidity(customError);
			refInput.inputElement.reportValidity();

			if (activeElement?.id !== refInput.inputElement.id) {
				refInput.inputElement.focus();
				refInput.inputElement.blur();

				if (activeElement.id) {
					activeElement.focus();
					if (activeElement.type === 'text') {
						activeElement.setSelectionRange(0, 0);
					}
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [customError]);

	let classNameLabel = classnames(
		{
			[`${CSS_CLASSES.LABEL}`]: true,
		},
		(!!field && !!field.value) || sValue
			? 'mdc-floating-label--float-above'
			: null,
		floatingLabelClassName
	);

	return (
		<StyleAuxiliary classes={CSS_CLASSES.CONTAINER}>
			<MDCTextField
				{...rest}
				floatingLabelClassName={classNameLabel}
				helperText={
					meta && meta.touched && meta.error ? (
						<HelperText
							persistent
							isValidationMessage={true}
							isValid={false}
							validation={true}
						>
							{meta.error}
						</HelperText>
					) : helperText ? (
						helperText
					) : help ? (
						<HelperText>{help}</HelperText>
					) : null
				}
				trailingIcon={
					canClear && sValue.trim() !== '' ? (
						trailingIcon && trailingIcon.props ? (
							<Icon {...trailingIcon.props} icon='clear' />
						) : (
							<Icon icon='clear' />
						)
					) : (
						trailingIcon
					)
				}
				onTrailingIconSelect={
					canClear && sValue.trim() !== ''
						? () => onClear()
						: rest.onTrailingIconSelect
				}
				valid={meta && meta.touched && meta.error ? meta.error : valid}
			>
				<Input
					{...field}
					{...inputProps}
					id={id ?? props.name}
					ref={input => (refInput = refInput ? refInput : input)}
					value={!!field && !!field.value ? field.value : sValue}
					onChange={onChangeHandler}
				/>
			</MDCTextField>
		</StyleAuxiliary>
	);
});

// documentation: https://github.com/material-components/material-components-web-react/tree/master/packages/text-field#props
TextField.propTypes = {
	canClear: PropTypes.bool,
	className: PropTypes.string,
	dense: PropTypes.bool,
	floatingLabelClassName: PropTypes.string,
	fullWidth: PropTypes.bool,
	help: PropTypes.string,
	helperText: PropTypes.element,
	inputProps: PropTypes.shape({
		className: PropTypes.string,
		disabled: PropTypes.func,
		handleFocusChange: PropTypes.func,
		id: PropTypes.string,
		inputType: PropTypes.string,
		isValid: PropTypes.bool,
		onBlur: PropTypes.func,
		onChange: PropTypes.func,
		onFocus: PropTypes.func,
		onMouseDown: PropTypes.func,
		onTouchStart: PropTypes.func,
		ref: PropTypes.func,
		setDisabled: PropTypes.func,
		setInputId: PropTypes.func,
		value: PropTypes.oneOf([PropTypes.number, PropTypes.string]),
	}),
	label: PropTypes.string,
	leadingIcon: PropTypes.element,
	lineRippleClassName: PropTypes.string,
	notchedOutlineClassName: PropTypes.string,
	onLeadingIconSelect: PropTypes.func,
	onChange: PropTypes.func,
	onTrailingIconSelect: PropTypes.func,
	outlined: PropTypes.bool,
	textarea: PropTypes.bool,
	trailingIcon: PropTypes.element,
};

export { HelperText };
export default TextField;
